import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { Company } from "../types";
import { Endpoint, setEndpoint } from "../services/endpoints";
import { deleteRequest, get, post, put } from "../services/restClient";

export interface CreateCompany {
    name: string;
    canCreateSubcompany?: boolean;
    createdBy?: string;
    emailaddresses?: string[];
    accessMarketplace?: boolean;
    collaboration?: string | null;
}

export interface EditCompany {
    name: string;
    canCreateSubcompany?: boolean;
}

export interface CreateSubCompany {
    name: string;
    superCompany: string;
    createdBy?: string;
}

const getCompanyById = async (companyId: string) => {
    const data = await get<Company>(setEndpoint(Endpoint.company, { companyId }), {
        requireAuth: true,
    });
    return data;
};

export function useGetCompany(companyId: string) {
    return useQuery({
        queryKey: ["companies", companyId],
        queryFn: () => getCompanyById(companyId),
    });
}

// A sub sub company (company -> sub company -> sub sub company) is the deepest level
// allowed, so it may not create sub companies of its own.
export function useIsSubSubCompany(companyId: string) {
    const { data: company, isLoading: isCompanyLoading } = useGetCompany(companyId);
    const superCompanyId = company?.superCompany;
    const { data: superCompany, isLoading: isSuperCompanyLoading } = useQuery({
        queryKey: ["companies", superCompanyId],
        queryFn: () => getCompanyById(superCompanyId ?? ""),
        enabled: !!superCompanyId,
    });

    return {
        isSubSubCompany: !!superCompany?.superCompany,
        isLoading: isCompanyLoading || (!!superCompanyId && isSuperCompanyLoading),
    };
}

const getTopCompany = async (companyId: string) => {
    const data = await get<Company>(setEndpoint(Endpoint.topCompany, { companyId }), {
        requireAuth: true,
    });
    return data;
};

export function useGetTopCompany(companyId: string) {
    return useQuery({
        queryKey: ["topCompany", companyId],
        queryFn: () => getTopCompany(companyId),
    });
}

const getCompanies = async () => {
    const data = await get<Company[]>(setEndpoint(Endpoint.companies), { requireAuth: true });
    return data;
};

export const useGetCompanies = () => {
    return useQuery({ queryKey: ["companies"], queryFn: () => getCompanies() });
};

export const useGetCompaniesByIds = (companyIds: string[]) => {
    const queries = useQueries({
        queries: (companyIds ?? []).map(companyId => ({
            queryKey: ["companies", companyId],
            queryFn: () => getCompanyById(companyId),
            enabled: !!companyId,
        })),
    });

    const isLoading = queries.some(q => q.isLoading);
    const isError = queries.some(q => q.isError);
    const error = queries.find(q => q.error)?.error;
    const data = queries.map(q => q.data ?? []).flat();

    return {
        data,
        isLoading,
        isError,
        error,
    };
};

const postCompany = async (company: CreateCompany) => {
    const data = await post<CreateCompany, Company>(setEndpoint(Endpoint.companies), company, {
        requireAuth: true,
    });
    return data;
};

export const useAddCompany = () => {
    const request = postCompany;
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ company }: { company: CreateCompany }) => {
            const result = await request(company);
            return result;
        },
        onSuccess: () => {
            return queryClient.invalidateQueries({
                queryKey: ["companies"],
            });
        },
    });
};

export const useUpdateCompany = () => {
    const request = putCompany;
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ companyId, company }: { companyId: string; company: EditCompany }) => {
            return request(companyId, company);
        },
        onSuccess: (data, { companyId }) => {
            void queryClient.invalidateQueries({ queryKey: ["companies"] });
            void queryClient.invalidateQueries({ queryKey: ["company", companyId] });
        },
    });
};

const putCompany = async (companyId: string, company: EditCompany) => {
    const data = await put<EditCompany, Company>(
        setEndpoint(Endpoint.company, { companyId }),
        company,
        { requireAuth: true },
    );
    return data;
};

export const useDeleteCompany = () => {
    const request = deleteCompany;
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ companyId }: { companyId: string }) => {
            return await request(companyId);
        },
        onSuccess: (data, { companyId }) => {
            void queryClient.invalidateQueries({ queryKey: ["companies"] });
            void queryClient.cancelQueries({ queryKey: ["company", companyId] });
        },
    });
};

const deleteCompany = async (companyId: string) => {
    const data = await deleteRequest(setEndpoint(Endpoint.company, { companyId }), {
        requireAuth: true,
    });
    return data;
};

const getSubCompanies = async (companyId: string) => {
    const data = await get<Company[]>(setEndpoint(Endpoint.subCompanies, { companyId }), {
        requireAuth: true,
    });
    return data;
};

export const useGetSubCompanies = (companyId: string) => {
    return useQuery({
        queryKey: ["subCompanies", companyId],
        queryFn: () => getSubCompanies(companyId),
    });
};

const getDescendantCompanies = async (companyId: string) => {
    const descendants: Company[] = [];
    const visited = new Set<string>([companyId]);
    let currentLevel = [companyId];

    while (currentLevel.length > 0) {
        const children = (await Promise.all(currentLevel.map(id => getSubCompanies(id)))).flat();
        const unvisited = children.filter(c => !visited.has(c.id));
        unvisited.forEach(c => visited.add(c.id));
        descendants.push(...unvisited);
        currentLevel = unvisited.map(c => c.id);
    }

    return descendants;
};

// All sub companies at every level below companyId (not including companyId itself).
export const useGetDescendantCompanies = (companyId: string, enabled = true) => {
    return useQuery({
        queryKey: ["subCompanies", companyId, "descendants"],
        queryFn: () => getDescendantCompanies(companyId),
        enabled: enabled && !!companyId,
    });
};

export const useGetAllSubCompanies = (companies?: Company[]) => {
    const queries = useQueries({
        queries: (companies ?? []).map(company => ({
            queryKey: ["subCompanies", company.id],
            queryFn: () => getSubCompanies(company.id),
            enabled: !!company.id,
        })),
    });

    const isLoading = queries.some(q => q.isLoading);
    const isError = queries.some(q => q.isError);
    const error = queries.find(q => q.error)?.error;
    const data = queries.map(q => q.data ?? []).flat();

    return {
        data,
        isLoading,
        isError,
        error,
    };
};

const createSubCompany = async (subCompany: CreateSubCompany) => {
    const superCompanyId: string = subCompany.superCompany;

    const data = await post<CreateSubCompany, Company>(
        setEndpoint(Endpoint.subCompany, { superCompanyId }),
        subCompany,
        { requireAuth: true },
    );
    return data;
};

export const useCreateSubCompany = () => {
    const request = createSubCompany;
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ subCompany }: { subCompany: CreateSubCompany }) => {
            const result = await request(subCompany);
            return result;
        },
        onSuccess: () => {
            return queryClient.invalidateQueries({ queryKey: ["subCompanies"] });
        },
    });
};
